import { IconButton } from '@creditornot/cb-button';
import { Cross, CaretDown, CaretUp } from '@creditornot/cb-icons';
import { colors, Elevation, cssThemedValue } from '@creditornot/cb-ingredients';
import { MenuItemGroup, MenuCheckboxItem } from '@creditornot/cb-menu';
import { Tag } from '@creditornot/cb-tag';
import R from 'ramda';
import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { DayOfWeek } from '../types';
import { DAYS_OF_WEEK } from '../values';

type Props = {
  className?: string;
  disabledWeekdays: DayOfWeek[];
  value: DayOfWeek[];
  onChange: (value: DayOfWeek[]) => void;
};

const daysOfWeek = {
  monday: { id: 'week.mon', default: 'Monday' },
  tuesday: { id: 'week.tue', default: 'Tuesday' },
  wednesday: { id: 'week.wed', default: 'Wednesday' },
  thursday: { id: 'week.thu', default: 'Thursday' },
  friday: { id: 'week.fri', default: 'Friday' },
  saturday: { id: 'week.sat', default: 'Saturday' },
  sunday: { id: 'week.sun', default: 'Sunday' },
};

const daysOfWeekShort = {
  monday: { id: 'week-short.mon', default: 'Mon' },
  tuesday: { id: 'week-short.tue', default: 'Tue' },
  wednesday: { id: 'week-short.wed', default: 'Wed' },
  thursday: { id: 'week-short.thu', default: 'Thu' },
  friday: { id: 'week-short.fri', default: 'Fri' },
  saturday: { id: 'week-short.sat', default: 'Sat' },
  sunday: { id: 'week-short.sun', default: 'Sun' },
};

const Root = styled.div`
  position: relative;
`;

const Placeholder = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
  margin-inline-start: 12px;
`;

const Icons = styled.div`
  display: flex;
  padding-inline-end: 8px;
`;

const ClearIconButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'grey',
  alignment: 'boundingBox',
})`
  margin-inline-end: 8px;
`;

const ClearIcon = styled(Cross).attrs({})``;

const ArrowIconButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'grey',
  background: 'staticWhite',
  alignment: 'boundingBox',
})``;

const DownArrowIcon = styled(CaretDown).attrs({})``;
const UpArrowIcon = styled(CaretUp).attrs({})``;

const Toggle = styled.div<{ open: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  min-height: 40px;
  cursor: pointer;
  border: 1px solid ${({ open }) => (open ? colors.wolt100 : cssThemedValue('borderPrimary'))};
`;

const Value = styled(Tag).attrs({ variant: 'lightBlue' })`
  margin: 6px 2px 6px 6px;
`;

const Values = styled.div``;

// TODO: Get rid of z-index
const Select = styled(Elevation).attrs({ level: 8 })`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  padding: 14px 16px;
  border-radius: 8px;
  z-index: 3;
`;

const WeekdayMultiSelect = ({ className, disabledWeekdays, value, onChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', handleOutsideClick, true);

    return () => window.removeEventListener('click', handleOutsideClick, true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const allDaysSelected = R.equals(new Set([...value, ...disabledWeekdays]), new Set(DAYS_OF_WEEK));

  return (
    <Root className={className} ref={componentRef}>
      <Toggle
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        open={isOpen}
      >
        <Values>
          {value.length ? (
            value
              .slice()
              .sort((a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b))
              .map(day => (
                <FormattedMessage key={day} id={daysOfWeekShort[day].id} defaultMessage={daysOfWeekShort[day].default}>
                  {message => (
                    <Value
                      onRemoveClick={event => {
                        event.stopPropagation();
                        onChange(value.filter(w => w !== day));
                      }}
                    >
                      {message}
                    </Value>
                  )}
                </FormattedMessage>
              ))
          ) : (
            <Placeholder>
              <FormattedMessage id="opening-hours.days" defaultMessage="Days" />
            </Placeholder>
          )}
        </Values>
        <Icons>
          {!!value.length && (
            <ClearIconButton
              onClick={e => {
                // Prevent submit if used inside <form> and prevent
                // the dropdown from opening or closing when clicked.
                e.stopPropagation();
                onChange([]);
              }}
            >
              <ClearIcon />
            </ClearIconButton>
          )}
          <ArrowIconButton>{isOpen ? <UpArrowIcon /> : <DownArrowIcon />}</ArrowIconButton>
        </Icons>
      </Toggle>
      {isOpen && (
        <Select>
          <MenuItemGroup>
            <MenuCheckboxItem
              checked={allDaysSelected}
              onClick={() => {
                onChange(
                  allDaysSelected
                    ? []
                    : DAYS_OF_WEEK.filter(day => !disabledWeekdays.includes(day) || value.includes(day)),
                );
              }}
            >
              <FormattedMessage id="weeklyScheduleInput.selectAll" defaultMessage="Select all" />
            </MenuCheckboxItem>
            {DAYS_OF_WEEK.map(day => (
              <MenuCheckboxItem
                key={day}
                checked={value.includes(day)}
                disabled={disabledWeekdays.includes(day) && !value.includes(day)}
                onClick={() => {
                  onChange(value.includes(day) ? value.filter(w => w !== day) : [...value, day]);
                }}
              >
                <FormattedMessage id={daysOfWeek[day].id} defaultMessage={daysOfWeek[day].default} />
              </MenuCheckboxItem>
            ))}
          </MenuItemGroup>
        </Select>
      )}
    </Root>
  );
};

export default WeekdayMultiSelect;
